import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useRecordDraw } from '../hooks/useRecordDraw';

interface RecordDrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: number | null;
}

/**
 * Modal for recording a draw (advance payment) for an estimator
 * Admin-only functionality
 */
export function RecordDrawModal({ isOpen, onClose, onSuccess, userId }: RecordDrawModalProps) {
  const [amount, setAmount] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [drawDate, setDrawDate] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');

  const { recordDraw, isLoading } = useRecordDraw();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!userId) {
      setError('No estimator selected');
      return;
    }

    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const success = await recordDraw({
      userId,
      amount: amountValue,
      referenceNumber: referenceNumber || undefined,
      notes: notes || undefined,
      drawDate: drawDate || undefined,
    });

    if (success) {
      resetForm();
      onSuccess();
    } else {
      setError('Failed to record draw. Please try again.');
    }
  };

  const resetForm = () => {
    setAmount('');
    setReferenceNumber('');
    setNotes('');
    setDrawDate(new Date().toISOString().split('T')[0]);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Draw</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="draw-amount" className="block text-sm font-medium text-gray-700 mb-1">
              Amount *
            </label>
            <Input
              id="draw-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label htmlFor="draw-reference" className="block text-sm font-medium text-gray-700 mb-1">
              Reference # (Check Number)
            </label>
            <Input
              id="draw-reference"
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="e.g., Check #1234"
            />
          </div>

          <div>
            <label htmlFor="draw-date" className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <Input
              id="draw-date"
              type="date"
              value={drawDate}
              onChange={(e) => setDrawDate(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="draw-notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <Textarea
              id="draw-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-secondary text-white hover:bg-secondary/90"
            >
              {isLoading ? 'Saving...' : 'Record Draw'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
